import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SIZES = {
  md: "480px",
  lg: "720px",
};

/**
 * Reusable modal shell: portal, backdrop close, ESC, focus trap, return focus, body scroll lock.
 */
export default function Modal({
  open,
  onClose,
  titleId,
  children,
  returnFocusRef,
  size = "lg",
  closeButtonRef,
}) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTarget = closeButtonRef?.current || dialogRef.current?.querySelector(FOCUSABLE);
    focusTarget?.focus();

    function handleKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;

      const focusables = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
      const returnEl = returnFocusRef?.current || previousFocusRef.current;
      if (returnEl && typeof returnEl.focus === "function") {
        returnEl.focus();
      }
    };
  }, [open, onClose, returnFocusRef, closeButtonRef]);

  if (!open) return null;

  const maxWidth = SIZES[size] || SIZES.lg;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "#00000088",
        display: "grid",
        placeItems: "center",
        padding: "24px",
      }}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          width: "100%",
          maxWidth,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-bright)",
          borderRadius: "8px",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
